/**
 * Critical Endpoints Configuration
 *
 * Defines which API endpoints are critical and should trigger the full-screen
 * error overlay when they fail. Non-critical endpoints continue to use toast notifications.
 */

import { AxiosRequestConfig } from 'axios';

/**
 * Configuration for a critical endpoint
 */
export interface CriticalEndpointConfig {
  /** Regex pattern to match the endpoint URL */
  pattern: RegExp;

  /** Tier level: 1 = critical (show overlay), 2 = degraded (toast), 3 = non-critical (toast) */
  tier: 1 | 2 | 3;

  /** HTTP method (optional - if not specified, matches all methods) */
  method?: string;

  /** Error messages for different error types */
  errorMessages: {
    /** Message for 5xx server errors */
    serverError: string;
    /** Message for network/timeout errors */
    networkError: string;
    /** Message for authentication failures */
    authError: string;
  };

  /** Whether the request can be retried */
  retryable: boolean;
}

/**
 * Registry of critical endpoints
 *
 * TIER 1 endpoints are essential for app functionality - failures show full-screen overlay
 * TIER 2 endpoints cause degraded functionality - failures show toast
 * TIER 3 endpoints are non-critical - failures show toast
 */
const CRITICAL_ENDPOINTS: CriticalEndpointConfig[] = [
  // ============================================================================
  // TIER 1: AUTHENTICATION (Blocking - App Cannot Function)
  // ============================================================================
  {
    pattern: /\/auth\/realms\/.*\/protocol\/openid-connect\/token/,
    tier: 1,
    method: 'POST',
    errorMessages: {
      serverError: 'Sistema de autenticação temporariamente indisponível. Tente novamente em alguns minutos.',
      networkError: 'Sem conexão com o servidor de autenticação. Verifique sua internet e tente novamente.',
      authError: 'Sua sessão expirou. Por favor, faça login novamente.'
    },
    retryable: true
  },

  // ============================================================================
  // TIER 1: USER ASSIGNMENTS (Blocking - Cannot Load User Permissions)
  // ============================================================================
  {
    pattern: /\/api\/v1\/users\/.*\/assignments/,
    tier: 1,
    method: 'GET',
    errorMessages: {
      serverError: 'Não foi possível carregar suas permissões de acesso. Tente novamente em alguns minutos.',
      networkError: 'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
      authError: 'Sua sessão expirou. Por favor, faça login novamente.'
    },
    retryable: true
  },

  // ============================================================================
  // TIER 1: CUSTOMER LIST (Blocking - Cannot Select Customer)
  // ============================================================================
  {
    pattern: /\/api\/v1\/customers$/,
    tier: 1,
    method: 'GET',
    errorMessages: {
      serverError: 'Não foi possível carregar a lista de clientes. Tente novamente em alguns minutos.',
      networkError: 'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
      authError: 'Sua sessão expirou. Por favor, faça login novamente.'
    },
    retryable: true
  },

  // ============================================================================
  // TIER 2: DASHBOARD STATS (Degraded - Dashboard Shows Empty State)
  // ============================================================================
  {
    pattern: /\/api\/v1\/dashboard\/stats/,
    tier: 2,
    method: 'GET',
    errorMessages: {
      serverError: 'Estatísticas temporariamente indisponíveis.',
      networkError: 'Sem conexão. Verifique sua internet.',
      authError: 'Sessão expirada. Faça login novamente.'
    },
    retryable: true
  },

  // ============================================================================
  // TIER 2: ACCESS LOGS (Degraded - Activity Feed Shows Empty)
  // ============================================================================
  {
    pattern: /\/api\/v1\/access-logs/,
    tier: 2,
    method: 'GET',
    errorMessages: {
      serverError: 'Logs de acesso temporariamente indisponíveis.',
      networkError: 'Sem conexão. Verifique sua internet.',
      authError: 'Sessão expirada. Faça login novamente.'
    },
    retryable: true
  }
];

/**
 * Checks if an API request is critical and returns its configuration
 *
 * @param config - The Axios request configuration
 * @returns The critical endpoint configuration if matched, or null if not critical
 *
 * @example
 * ```typescript
 * const criticalConfig = isCriticalRequest(axiosConfig);
 * if (criticalConfig && criticalConfig.tier === 1) {
 *   // This is a critical endpoint failure - show full-screen overlay
 *   showErrorOverlay(criticalConfig.errorMessages.serverError);
 * }
 * ```
 */
export function isCriticalRequest(config: AxiosRequestConfig): CriticalEndpointConfig | null {
  const url = config.url || '';
  const method = (config.method || 'GET').toUpperCase();

  for (const endpoint of CRITICAL_ENDPOINTS) {
    // Check if URL matches pattern
    if (!endpoint.pattern.test(url)) {
      continue;
    }

    // Check if method matches (if method is specified)
    if (endpoint.method && endpoint.method.toUpperCase() !== method) {
      continue;
    }

    // Only return if this is a TIER 1 (critical) endpoint
    // TIER 2 and 3 endpoints should use normal toast error handling
    if (endpoint.tier === 1) {
      return endpoint;
    }
  }

  return null;
}

/**
 * Get all endpoints of a specific tier
 *
 * @param tier - The tier to filter by (1, 2, or 3)
 * @returns Array of endpoint configurations for that tier
 */
export function getEndpointsByTier(tier: 1 | 2 | 3): CriticalEndpointConfig[] {
  return CRITICAL_ENDPOINTS.filter(endpoint => endpoint.tier === tier);
}
